// src/components/GemelData/MiniGemelDataFacetsList.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Chip, List, Typography } from "@mui/material";

import LoadingIndicator from "../../shared/UIParts/LoadingIndicator";
import ErrorAlert from "../../shared/UIParts/ErrorAlert";
import SearchField from "../../shared/UIParts/SearchField";
import { formatProductType, fundsFacetFormatter } from "../../utils/enumFormatter";
import { FundFacetListItem } from "../../shared/UIParts/FundFacetListItem";
import { useAllFundsQuery } from "../../hooks/useFunds";

import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { ProductType } from "../../interfaces/DTO/mislaka_xml_models";
import { useFacetFiltersStore, useSelectedFundFacetStore } from "../../stores/fundFacetStore";
 // ✅ גלובלי

export enum FacetType {
  FundClassification = "fund_classification",
  FundName = "fund_name",
  SubSpecialization = "sub_specialization",
  ManagingCorporation = "managing_corporation",
  ProductType = "product_type",
}

// תצוגה מהירה לקרן + crumbs (כמו שהגדרנו קודם)
export interface FundPreview {
  fund_id: number;
  fund_name: string;
  product_type?: ProductType | null;
  managing_corporation?: string | null;
  managing_corporation_legal_id?: number | string | null;
  sub_specialization?: string | null;
  yields?: {
    ytd?: number | null;
    y3?: number | null;
  };
}
export interface FacetCrumb {
  facetType: FacetType;
  text: string;
  value: any;
}

export interface FundFacetItem {
  facetType: FacetType;
  facetName: string;
  facetValue: any;
  displayedText: string;
  productType?: ProductType;

  fundPreview?: FundPreview;
  crumbs?: FacetCrumb[];
  searchTextLower?: string;
  sortKey?: string | number;
}

export enum TargetPopulationFilter {
  All = "all",
  General = "general",
}

const MiniGemelDataFacetsList: React.FC = () => {
  const { data: allFunds = [], isLoading, error } = useAllFundsQuery();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedFacet, setSelectedFacet] = useState<string | null>(fundsFacetFormatter(FacetType.ProductType));
  const [reportPeriod, setReportPeriod] = useState<string | null>(null);
  const [targetPopulationFilter, setTargetPopulationFilter] =
    useState<TargetPopulationFilter>(TargetPopulationFilter.General);

  // ✅ פילטרים גלובליים
  const { activeFacetFilters, addFilter, removeFilter, replaceFilters } = useFacetFiltersStore();
  // ✅ פריט נבחר לדף הראשי
  const { setSelectedItem } = useSelectedFundFacetStore();

  useEffect(() => {
    if (allFunds.length > 0 && !reportPeriod) {
      const period = allFunds[0].report_period;
      if (period) setReportPeriod(period);
    }
  }, [allFunds, reportPeriod]);

  const handleChipClick = (facetName: string) => {
    setSelectedFacet((prev) => (prev === facetName ? null : facetName));
  };

  // ✅ בחירת פריט: גם מוסיף לפילטרים וגם מעדכן את הדף
  const handleSelectFacet = (item: FundFacetItem) => {
    if (!activeFacetFilters.some((i) => i.facetType === item.facetType && i.facetValue === item.facetValue)) {
      addFilter(item);
    }
    setSelectedItem(item);
  };

  // ✅ הסרת פילטר
  const handleRemoveFacet = (facetValueToRemove: any, facetTypeToRemove: FacetType) => {
    removeFilter(facetTypeToRemove, facetValueToRemove);
  };

  // ✅ פונקציה להחלפת כל הפילטרים (לשימוש ע"י breadcrumbs בתוך ה־ListItem)
  const handleReplaceActiveFilters = (newItem: FundFacetItem) => {
    replaceFilters([newItem]);
    setSelectedItem(newItem);
  };

  // 🔎 סינון הקרנות
  const filteredFunds = useMemo(() => {
    return allFunds.filter((fund) => {
      if (targetPopulationFilter === TargetPopulationFilter.General) {
        // ציבורי = ערך ריק/null
        return !fund.target_population;
      }

      return activeFacetFilters.every((facet) => {
        switch (facet.facetType) {
          case FacetType.FundName:
            return fund.fund_id === facet.facetValue;
          case FacetType.ManagingCorporation:
            return String(fund.managing_corporation_legal_id) === String(facet.facetValue);
          case FacetType.FundClassification:
            return fund.fund_classification === facet.facetValue;
          case FacetType.SubSpecialization:
            return fund.sub_specialization === facet.facetValue;
          case FacetType.ProductType:
            return fund.product_type === facet.facetValue;
          default:
            return true;
        }
      });
    });
  }, [allFunds, activeFacetFilters, targetPopulationFilter]);

  // 🧱 בניית פריטי פייסטות עשירים
  const facets = useMemo(() => {
    const getUniqueValues = <T extends keyof FundDataBasic>(field: T): FundFacetItem[] => {
      const seen = new Set<any>();

      return filteredFunds
        .filter((fund) => {
          const rawVal = fund[field];
          const key =
            field === ("fund_name" as T)
              ? fund.fund_id
              : field === ("managing_corporation" as T)
              ? fund.managing_corporation_legal_id
              : rawVal;

          if (key && !seen.has(key)) {
            seen.add(key);
            return true;
          }
          return false;
        })
        .map((fund) => {
          const facetType: FacetType =
            field === ("fund_name" as T)
              ? FacetType.FundName
              : field === ("managing_corporation" as T)
              ? FacetType.ManagingCorporation
              : field === ("product_type" as T)
              ? FacetType.ProductType
              : field === ("fund_classification" as T)
              ? FacetType.FundClassification
              : FacetType.SubSpecialization;

          const facetValue =
            facetType === FacetType.FundName
              ? fund.fund_id
              : facetType === FacetType.ManagingCorporation
              ? fund.managing_corporation_legal_id
              : facetType === FacetType.ProductType
              ? fund.product_type
              : (fund as any)[field];

          const displayedText =
            facetType === FacetType.FundName
              ? fund.fund_name
              : facetType === FacetType.ManagingCorporation
              ? fund.managing_corporation ?? "—"
              : facetType === FacetType.ProductType
              ? formatProductType(fund.product_type)
              : String((fund as any)[field] ?? "—");

          const crumbs: FacetCrumb[] = [
            {
              facetType: FacetType.ManagingCorporation,
              text: fund.managing_corporation ?? "—",
              value: fund.managing_corporation_legal_id,
            },
            {
              facetType: FacetType.ProductType,
              text: formatProductType(fund.product_type),
              value: fund.product_type ?? "",
            },
            {
              facetType: FacetType.SubSpecialization,
              text: fund.sub_specialization ?? "—",
              value: fund.sub_specialization ?? "",
            },
            {
              facetType: FacetType.FundName,
              text: fund.fund_name,
              value: fund.fund_id,
            },
          ];

          const fundPreview: FundPreview | undefined =
            facetType === FacetType.FundName
              ? {
                  fund_id: fund.fund_id,
                  fund_name: fund.fund_name,
                  product_type: fund.product_type,
                  managing_corporation: fund.managing_corporation,
                  managing_corporation_legal_id: fund.managing_corporation_legal_id,
                  sub_specialization: fund.sub_specialization,
                  yields: {
                    ytd: typeof fund.year_to_date_yield === "number" ? fund.year_to_date_yield : null,
                    y3: typeof fund.yield_trailing_3_yrs === "number" ? fund.yield_trailing_3_yrs : null,
                  },
                }
              : undefined;

          return {
            facetType,
            facetName: fundsFacetFormatter(facetType),
            facetValue,
            displayedText,
            productType: fund.product_type,
            crumbs,
            fundPreview,
            searchTextLower: (displayedText ?? "").toString().toLowerCase(),
            sortKey:
              facetType === FacetType.FundName
                ? fund.fund_name
                : facetType === FacetType.ManagingCorporation
                ? fund.managing_corporation
                : displayedText,
          } as FundFacetItem;
        });
    };

    return [
      ...getUniqueValues("product_type"),
      ...getUniqueValues("fund_name"),
      ...getUniqueValues("fund_classification"),
      ...getUniqueValues("sub_specialization"),
      ...getUniqueValues("managing_corporation"),
    ];
  }, [filteredFunds]);

  // 🔎 חיפוש יעיל
  const filteredList = facets.filter(
    (item) =>
      (!selectedFacet || item.facetName === selectedFacet) &&
      ((item.searchTextLower ?? "").includes(searchQuery.toLowerCase()) ||
        String(item.facetValue ?? "").includes(searchQuery)) &&
      !activeFacetFilters.some(
        (selected) => selected.facetType === item.facetType && selected.facetValue === item.facetValue
      )
  );

  if (isLoading) return <LoadingIndicator />;
  if (error) return <ErrorAlert message={error.message || "שגיאה בטעינת נתונים"} />;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto", height: "100%" }}>
      {/* פס עליון: אוכלוסיה + תקופה + חיפוש */}
      <Box sx={{ margin: "16px 16px -6px 16px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, mb: 1 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              label="כלל האוכלוסיה"
              variant={targetPopulationFilter === TargetPopulationFilter.General ? "filled" : "outlined"}
              onClick={() => setTargetPopulationFilter(TargetPopulationFilter.General)}
            />
            <Chip
              label="הכל"
              variant={targetPopulationFilter === TargetPopulationFilter.All ? "filled" : "outlined"}
              onClick={() => setTargetPopulationFilter(TargetPopulationFilter.All)}
            />
          </Box>
          {reportPeriod && (
            <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: "nowrap" }}>
              {`נכון לתקופה: ${reportPeriod}`}
            </Typography>
          )}
        </Box>

        <SearchField
          placeholder="חפש נתונים בגמל..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </Box>

      {/* טאבים של סוגי פייסטות */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
        {[...new Set(facets.map((f) => f.facetName))].map((facetName, index) => {
          const count = facets.filter((f) => f.facetName === facetName).length;
          const isSelected = selectedFacet === facetName;

          return (
            <Chip
              key={index}
              label={`${facetName} (${count})`}
              onClick={() => handleChipClick(facetName)}
              sx={{
                backgroundColor: isSelected ? "#80deea" : "#e0f7fa",
                fontWeight: isSelected ? "bold" : "normal",
                cursor: "pointer",
              }}
            />
          );
        })}
      </Box>

      {/* פייסטות נבחרות */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, px: 2 }}>
        {activeFacetFilters.map((item) => (
          <Chip
            key={`${item.facetType}_${item.facetValue}`}
            label={item.displayedText}
            onDelete={() => handleRemoveFacet(item.facetValue, item.facetType)}
            color="primary"
          />
        ))}
      </Box>

      {/* הרשימה */}
      <List sx={{ height: "100%", overflowY: "auto" }}>
        {filteredList.map((item) => (
          <FundFacetListItem
            key={`${item.facetType}_${item.facetValue}`}
            item={item}
            isSelected={activeFacetFilters.some(
              (i) => i.facetType === item.facetType && i.facetValue === item.facetValue
            )}
            onSelect={handleSelectFacet}
            onReplaceActiveFilters={handleReplaceActiveFilters} // ✅ לשימוש ע״י ה־Breadcrumbs בתוך הרשומה
          />
        ))}
      </List>
    </Box>
  );
};

export default MiniGemelDataFacetsList;
